import { useState, useMemo } from 'react'
import { X, CalendarDays, ChevronDown, Trash2 } from '@/components/icons'
import { toast } from 'sonner'
import { Button } from './ui/button'
import { Avatar, AvatarFallback } from './ui/avatar'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import dayjs from 'dayjs'
import { useRosterCheckExistingInRange, useRosterGetHistory, useRosterDeleteHistory } from '../lib/hooks/roster'
import { DutyStatus } from '@@api/shared'
import { crewRoleLabels } from '@@api/shared'
import { getRosterDutyStatusColor, getRosterStatusBadge, formatDutyStatus } from '../utils/statusColors'
import { getInitials } from '../lib/utils'
import { EmptyState } from './EmptyState'
import { DetailSkeleton } from './ui/skeleton-presets'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible'
import { RosterEntryDrawer } from './RosterEntryDrawer'

interface CrewWithRosterParams {
  id: string
  firstName: string
  lastName: string
  name: string
  avatarColor: string
  crewRole: string
}

interface RosterHistoryEntry {
  id: string
  startDate: string
  endDate: string
  dutyStatus: DutyStatus | null
  patternDaysOn: number | null
  patternDaysOff: number | null
  aircraft?: { registration: string } | null
  createdAt: string | Date
}

/** Defaults from a roster history entry for pre-filling forms */
export function getRosterDefaults(entry: RosterHistoryEntry | null | undefined) {
  if (!entry) {
    return {
      startDate: null as Date | null,
      endDate: null as Date | null,
      dutyStatus: DutyStatus.OnDuty,
      patternDaysOn: null as number | null,
      patternDaysOff: null as number | null,
    }
  }
  return {
    startDate: new Date(entry.startDate),
    endDate: new Date(entry.endDate),
    dutyStatus: entry.dutyStatus ?? DutyStatus.OnDuty,
    patternDaysOn: entry.patternDaysOn,
    patternDaysOff: entry.patternDaysOff,
  }
}

function getPeriodBadge(entry: RosterHistoryEntry): { days: string; label: string } {
  // On/Off pattern blocks show the rotation length
  if (entry.patternDaysOn != null && entry.patternDaysOff != null) {
    return {
      days: `${entry.patternDaysOn} Days`,
      label: 'On/Off',
    }
  }
  // Plain blocks show the total period length and the duty status
  const totalDays = dayjs(entry.endDate).diff(dayjs(entry.startDate), 'day') + 1
  return {
    days: `${totalDays} ${totalDays === 1 ? 'Day' : 'Days'}`,
    label: entry.dutyStatus ? formatDutyStatus(entry.dutyStatus) : 'Off',
  }
}

function RosterHistoryItemCard({ entry, variant = 'current', onDelete }: { entry: RosterHistoryEntry; variant?: 'current' | 'history'; onDelete?: () => void }) {
  const badge = getPeriodBadge(entry)
  return (
    <div className="bg-content-primary-inverse flex items-center justify-between pl-4 pr-2 py-2 rounded-2xl">
      <div className="flex gap-3 items-center py-1 min-w-0">
        {variant === 'current' && (
          <CalendarDays className="size-5 text-content-primary-subtle shrink-0" />
        )}
        <div className="flex flex-col min-w-0">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-content-primary-bold leading-normal whitespace-nowrap">
              {dayjs(entry.startDate).format('MMM DD YYYY')}
            </span>
            <span className="text-sm text-content-primary-bold leading-normal">-</span>
            <span className="text-sm text-content-primary-bold leading-normal whitespace-nowrap">
              {dayjs(entry.endDate).format('MMM DD YYYY')}
            </span>
          </div>
          {entry.aircraft?.registration && (
            <span className="text-xs text-content-primary-subtle leading-normal">
              {entry.aircraft.registration}
            </span>
          )}
        </div>
      </div>
      {variant === 'current' && (
        <div className="flex gap-2 items-center shrink-0">
          {badge && (
            <div className="bg-surface-L2 flex flex-col items-start justify-center p-2 rounded-lg w-[70px]">
              <span className="font-semibold text-base text-content-primary-bold leading-normal tracking-[-0.5px] whitespace-nowrap">
                {badge.days}
              </span>
              <span className="font-semibold text-xs text-content-primary-subtle leading-normal whitespace-nowrap">
                {badge.label}
              </span>
            </div>
          )}
          {onDelete && (
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              className="size-8 shrink-0"
              onClick={onDelete}
              aria-label="Delete roster block"
            >
              <Trash2 className="size-4 text-content-negative-bold" />
            </Button>
          )}
        </div>
      )}
      {variant === 'history' && (
        <div className="flex gap-2 items-center shrink-0">
          {badge && (
            <div className="flex gap-1 items-center py-1">
              <span className="text-sm text-content-primary-bold leading-normal">
                {badge.days}
              </span>
              <span className="text-sm text-content-primary-subtle leading-normal">
                {badge.label}
              </span>
            </div>
          )}
          {onDelete && (
            <Button
              variant="secondary"
              size="icon"
              roundness="round"
              className="size-8 shrink-0"
              onClick={onDelete}
              aria-label="Delete roster block"
            >
              <Trash2 className="size-4 text-content-negative-bold" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface RosterStatusDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  crew: CrewWithRosterParams | null
  defaultAircraftId?: string | null
}

export function RosterStatusDrawer({
  open,
  onOpenChange,
  crew,
  defaultAircraftId,
}: RosterStatusDrawerProps) {
  const { data: history, isLoading: isLoadingHistory } = useRosterGetHistory(crew?.id ?? null)
  const [showRosterEntry, setShowRosterEntry] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null)
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null)
  const [entryToDelete, setEntryToDelete] = useState<RosterHistoryEntry | null>(null)

  const { mutate: deleteHistory, isPending: isDeletingHistory } = useRosterDeleteHistory()

  const handleConfirmDelete = () => {
    if (!entryToDelete) return
    deleteHistory(
      { id: entryToDelete.id },
      {
        onSuccess: () => {
          setEntryToDelete(null)
          toast.success('Roster block removed and schedule rebuilt.')
        },
        onError: () => {
          toast.error('Could not remove this roster block. Please try again.')
        },
      }
    )
  }

  const latestEntry = history?.[0] ?? null
  const defaults = latestEntry ? getRosterDefaults(latestEntry) : null

  // Span across every history block so the summary aggregates all duty statuses,
  // not just the latest block. Dates are YYYY-MM-DD strings → lexicographic min/max.
  const fullRange = useMemo(() => {
    if (!history || history.length === 0) return { start: null as string | null, end: null as string | null }
    let start = history[0].startDate
    let end = history[0].endDate
    for (const h of history) {
      if (h.startDate < start) start = h.startDate
      if (h.endDate > end) end = h.endDate
    }
    return { start, end }
  }, [history])

  const { data: existingRosterInfo, isLoading: isLoadingRosterInfo } = useRosterCheckExistingInRange(
    crew?.id ?? null,
    fullRange.start,
    fullRange.end
  )

  const initials = crew ? getInitials(crew.firstName, crew.lastName) : ''
  const displayName = crew
    ? crew.firstName && crew.lastName
      ? `${crew.firstName} ${crew.lastName}`
      : crew.name
    : ''
  const roleLabel = crew ? crewRoleLabels[crew.crewRole] ?? crew.crewRole : ''

  const currentBadge = useMemo(() => {
    if (!defaults) return null
    return getRosterStatusBadge(defaults.dutyStatus)
  }, [defaults])

  const isLoading = isLoadingHistory || isLoadingRosterInfo
  const statusCounts = existingRosterInfo?.statusCounts ?? {}
  const hasHistory = history && history.length > 0

  const handleScheduleClick = () => {
    const tomorrow = dayjs().add(1, 'day').startOf('day').toDate()
    setSelectedStartDate(tomorrow)
    setSelectedEndDate(tomorrow)
    setShowRosterEntry(true)
  }

  const handleRosterEntrySuccess = () => {
    setShowRosterEntry(false)
    setSelectedStartDate(null)
    setSelectedEndDate(null)
  }

  if (!crew) return null

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 rounded-tl-2xl rounded-tr-2xl shadow-lg max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="font-semibold text-lg text-content-primary-bold">
              Schedule
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="secondary" size="icon" roundness="round" className="size-8">
                <X className="w-4 h-4" />
              </Button>
            </DrawerClose>
          </DrawerHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="space-y-4">
              {isLoading ? (
                <DetailSkeleton rows={5} />
              ) : hasHistory ? (
                <>
                  {/* Crew Card */}
                  <div className="bg-surface-L0 border border-border-L1 rounded-2xl px-4 py-2">
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Avatar className="size-8 shrink-0"><AvatarFallback className="text-xs text-background font-medium" style={crew.avatarColor ? { backgroundColor: crew.avatarColor } : undefined}>{initials}</AvatarFallback></Avatar>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm text-content-primary-bold leading-normal tracking-[0.07px]">
                            {displayName}
                          </span>
                          <span className="text-xs text-content-primary-subtle leading-normal tracking-[0.18px]">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                      {currentBadge && (
                        <span className={`inline-flex items-center px-2 py-[3px] rounded-full text-xs font-medium leading-normal tracking-[0.18px] ${currentBadge.className}`}>
                          {currentBadge.text}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Current Roster Card */}
                  {latestEntry && (
                    <RosterHistoryItemCard
                      entry={latestEntry}
                      variant="current"
                      onDelete={() => setEntryToDelete(latestEntry)}
                    />
                  )}

                  {/* Collapsible History */}
                  {history.length > 1 && (
                    <Collapsible>
                      <div className="border border-border-L1 rounded-2xl px-4 pt-3 pb-4">
                        <CollapsibleTrigger className="flex items-center justify-between w-full">
                          <span className="text-sm font-bold text-content-primary-bold leading-normal">
                            History
                          </span>
                          <ChevronDown className="size-6 text-content-primary-bold transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div
                            className="flex flex-col gap-4 pt-4 max-h-[280px] overflow-y-auto overscroll-contain"
                            style={{ WebkitOverflowScrolling: 'touch' }}
                          >
                            {history.slice(1).map((entry) => (
                              <RosterHistoryItemCard
                                key={entry.id}
                                entry={entry}
                                variant="history"
                                onDelete={() => setEntryToDelete(entry)}
                              />
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Roster Summary Legend (totals across the full roster span) */}
                  {Object.keys(statusCounts).length > 0 && (
                    <div className="flex gap-1 px-1 pt-1">
                      <div className="flex-1 flex flex-col gap-1">
                        {statusCounts[DutyStatus.OnDuty] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.OnDuty)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              On duty {statusCounts[DutyStatus.OnDuty]} days
                            </span>
                          </div>
                        )}
                        {statusCounts[DutyStatus.OffDuty] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.OffDuty)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Off duty {statusCounts[DutyStatus.OffDuty]} days
                            </span>
                          </div>
                        )}
                        {statusCounts[DutyStatus.Leave] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.Leave)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Leave {statusCounts[DutyStatus.Leave]} days
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-1">
                        {statusCounts[DutyStatus.Training] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.Training)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Training {statusCounts[DutyStatus.Training]} days
                            </span>
                          </div>
                        )}
                        {statusCounts[DutyStatus.Standby] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.Standby)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Standby {statusCounts[DutyStatus.Standby]} days
                            </span>
                          </div>
                        )}
                        {statusCounts[DutyStatus.Sick] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.Sick)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Sick {statusCounts[DutyStatus.Sick]} days
                            </span>
                          </div>
                        )}
                        {statusCounts[DutyStatus.Positioning] != null && (
                          <div className="flex items-center gap-2 py-1 rounded-lg">
                            <div className={`size-3 rounded ${getRosterDutyStatusColor(DutyStatus.Positioning)}`} />
                            <span className="text-xs font-semibold text-content-primary-bold leading-normal">
                              Positioning {statusCounts[DutyStatus.Positioning]} days
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Empty State */
                <EmptyState
                  icon={CalendarDays}
                  title="Nothing scheduled yet"
                  description="Add a schedule by tapping below."
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <DrawerFooter>
            <Button
              variant="primary"
              size="mini"
              onClick={handleScheduleClick}
              className="w-full"
            >
              {hasHistory ? 'Edit Schedule' : 'Create Schedule'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Roster Entry Drawer (after dates selected) */}
      <RosterEntryDrawer
        open={showRosterEntry}
        onOpenChange={(open) => {
          setShowRosterEntry(open)
          if (!open) {
            setSelectedStartDate(null)
            setSelectedEndDate(null)
          }
        }}
        defaultCrewId={crew.id}
        defaultAircraftId={defaultAircraftId}
        defaultStartDate={selectedStartDate}
        defaultEndDate={selectedEndDate}
        defaultDutyStatus={defaults?.dutyStatus}
        defaultPatternDaysOn={defaults?.patternDaysOn}
        defaultPatternDaysOff={defaults?.patternDaysOff}
        onSuccess={handleRosterEntrySuccess}
      />

      {/* Delete roster block confirmation */}
      <AlertDialog
        open={entryToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !isDeletingHistory) setEntryToDelete(null)
        }}
      >
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove This Roster Block</AlertDialogTitle>
            <AlertDialogDescription>
              {displayName}&apos;s roster will be rebuilt from the remaining history as if this
              block never existed. Assigned flights won&apos;t change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingHistory}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleConfirmDelete()
              }}
              disabled={isDeletingHistory}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
